import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import * as Sentry from "@sentry/cloudflare";

export type LeaderboardEntry = {
  position: number;
  creator_id: string;
  handle: string;
  name: string;
  avatar_url: string | null;
  category: string | null;
  total_commission: number;
};

export const getLeaderboard = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const { data, error } = await supabaseAdmin.rpc("get_creator_leaderboard", { _limit: 10 });

  if (!error && data) {
    return ((data ?? []) as Array<{
      creator_id: string;
      total_commission: number | string;
      handle: string | null;
      name: string | null;
      avatar_url: string | null;
      categories: string[] | null;
    }>).map((row, idx) => ({
      position: idx + 1,
      creator_id: row.creator_id,
      handle: row.handle ?? "",
      name: row.name ?? "",
      avatar_url: row.avatar_url ?? null,
      category: row.categories?.[0] ?? null,
      total_commission: Number(row.total_commission),
    })) satisfies LeaderboardEntry[];
  }

  // Fallback: aggregate in JS if the RPC is missing or errored.
  Sentry.captureException(error);
  const { data: rows, error: fbErr } = await supabaseAdmin
    .from("commissions")
    .select("creator_id, commission_amount, status, creator:creators!inner(id, handle, name, avatar_url, status, categories)")
    .eq("status", "paid")
    .limit(50000);
  if (fbErr || !rows) return [] as LeaderboardEntry[];

  type Row = {
    creator_id: string;
    commission_amount: number | string;
    creator: { id: string; handle: string; name: string; avatar_url: string | null; status: string; categories: string[] | null } | null;
  };
  const agg = new Map<string, { creator: NonNullable<Row["creator"]>; total: number }>();
  for (const r of rows as unknown as Row[]) {
    const cr = r.creator;
    if (!cr || cr.status !== "live") continue;
    const e = agg.get(cr.id) ?? { creator: cr, total: 0 };
    e.total += Number(r.commission_amount || 0);
    agg.set(cr.id, e);
  }
  return Array.from(agg.values())
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)
    .map((e, idx) => ({
      position: idx + 1,
      creator_id: e.creator.id,
      handle: e.creator.handle ?? "",
      name: e.creator.name ?? "",
      avatar_url: e.creator.avatar_url,
      category: e.creator.categories?.[0] ?? null,
      total_commission: Math.round(e.total * 100) / 100,
    })) satisfies LeaderboardEntry[];
});

/** Returns the signed-in creator's position in the all-creators leaderboard, or null. */
export const getMyLeaderboardRank = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: me } = await supabaseAdmin
      .from("creators")
      .select("id")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!me?.id) return null;

    const { data: rows, error } = await supabaseAdmin
      .from("commissions")
      .select("creator_id, commission_amount, creator:creators!inner(status)")
      .eq("status", "paid")
      .limit(50000);
    if (error || !rows) return null;

    const totals = new Map<string, number>();
    for (const r of rows as unknown as Array<{ creator_id: string; commission_amount: number | string; creator: { status: string } | null }>) {
      if (r.creator?.status !== "live") continue;
      totals.set(r.creator_id, (totals.get(r.creator_id) ?? 0) + Number(r.commission_amount || 0));
    }
    const ranked = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
    const idx = ranked.findIndex(([id]) => id === me.id);
    if (idx === -1) return { rank: null as number | null, total: 0 };
    return { rank: idx + 1, total: Math.round(ranked[idx][1] * 100) / 100 };
  });
